import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Globe, Copy, Trash2, Square, ArrowLeft, Download, Pencil,
  FileCode2, HardDrive, Sparkles, CircleDot,
} from "lucide-react";
import { cloner, zipUrl } from "@/lib/clonerApi";
import { apiError, userApi } from "@/lib/userApi";
import { CircuitBg } from "@/components/CircuitBg";

const TIERS = [
  { pages: 1, credits: 400, label: "Solo esta página" },
  { pages: 10, credits: 500, label: "10 páginas" },
  { pages: 30, credits: 600, label: "30 páginas" },
  { pages: 100, credits: 700, label: "100 páginas" },
  { pages: 300, credits: 800, label: "300 páginas" },
];

const STATUS = {
  CLONANDO: { text: "Clonando", color: "text-amber-300", dot: "bg-amber-400" },
  COMPLETO: { text: "Completo", color: "text-emerald-300", dot: "bg-emerald-400" },
  DETENIDO: { text: "Detenido", color: "text-white/60", dot: "bg-white/40" },
  ERROR: { text: "Error", color: "text-rose-400", dot: "bg-rose-500" },
};

const kb = (n) => {
  if (!n) return "0 KB";
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
};

const SiteCard = ({ site, onDelete, onStop, onRename }) => {
  const st = STATUS[site.status] || STATUS.DETENIDO;
  const pct = site.max_pages ? Math.min(100, (site.pages_done / site.max_pages) * 100) : 0;
  return (
    <div className="glass-card forge-card p-4" data-testid={`clone-card-${site.site_id}`}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-black/40 flex items-center justify-center shrink-0">
          <Globe className="w-5 h-5 text-[#FF8A2B]" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-white font-semibold truncate">{site.name}</span>
            <button onClick={() => onRename(site)} data-testid={`clone-rename-${site.site_id}`}
              className="text-white/35 hover:text-[#FF8A2B]"><Pencil className="w-3 h-3" /></button>
          </div>
          <div className="text-[11px] text-white/40 truncate">{site.url}</div>
          <div className={`text-[11px] mt-1 flex items-center gap-1.5 ${st.color}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${st.dot} ${
              site.status === "CLONANDO" ? "animate-pulse" : ""}`} />
            {st.text}
            {site.status === "CLONANDO" && site.current && (
              <span className="text-white/30 truncate">· {site.current.slice(0, 40)}</span>
            )}
          </div>
        </div>
      </div>

      {site.status === "CLONANDO" && (
        <div className="h-1 bg-white/10 rounded-full mt-3 overflow-hidden">
          <div className="h-full bg-[#FF8A2B] transition-all" style={{ width: `${pct}%` }} />
        </div>
      )}

      <div className="flex items-center gap-4 text-[11px] text-white/50 mt-3">
        <span className="flex items-center gap-1"><FileCode2 className="w-3 h-3" />
          {site.pages_done}/{site.max_pages} págs</span>
        <span>{site.files} archivos</span>
        <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" />{kb(site.bytes)}</span>
      </div>
      {site.error && <div className="text-[11px] text-rose-400 mt-2">{site.error}</div>}

      <div className="flex items-center gap-2 mt-3">
        <Link to={`/clonador/${site.site_id}`} data-testid={`clone-open-${site.site_id}`}
          className="glass-btn glass-btn-red px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> Abrir editor
        </Link>
        <a href={zipUrl(site.site_id)} data-testid={`clone-zip-${site.site_id}`}
          className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
          <Download className="w-3.5 h-3.5" /> ZIP
        </a>
        {site.status === "CLONANDO" && (
          <button onClick={() => onStop(site.site_id)} data-testid={`clone-stop-${site.site_id}`}
            className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5 text-amber-300">
            <Square className="w-3.5 h-3.5" /> Detener
          </button>
        )}
        <button onClick={() => onDelete(site.site_id)} data-testid={`clone-del-${site.site_id}`}
          className="glass-btn px-3 py-2 rounded-xl text-xs text-white/60 hover:text-rose-400 ml-auto">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default function Cloner() {
  const [url, setUrl] = useState("");
  const [tier, setTier] = useState(1);
  const [withJs, setWithJs] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sites, setSites] = useState([]);
  const [credits, setCredits] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try { setSites((await cloner.sites()).data.sites); } catch { /* sesión */ }
  };
  const loadCredits = () => userApi.get("/user/auth/me")
    .then(({ data }) => setCredits(data.credits)).catch(() => {});
  useEffect(() => {
    load();
    loadCredits();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!url.trim()) return toast.error("Escribe la dirección del sitio");
    setBusy(true);
    try {
      const { data } = await cloner.create({ url, max_pages: tier, with_js: withJs });
      toast.success(`Copiando el sitio · ${data.credits_charged} créditos`);
      setUrl("");
      load();
      loadCredits();
    } catch (e) { toast.error(apiError(e, "No se pudo empezar la copia")); }
    finally { setBusy(false); }
  };

  const rename = async (site) => {
    const name = window.prompt("Nuevo nombre del sitio", site.name);
    if (!name) return;
    await cloner.rename(site.site_id, name);
    load();
  };
  const del = async (id) => {
    if (!window.confirm("¿Borrar este sitio copiado? No se puede deshacer.")) return;
    await cloner.remove(id);
    load();
  };
  const stop = async (id) => { await cloner.stop(id); load(); };

  const tierCredits = TIERS.find((t) => t.pages === tier)?.credits;

  return (
    <div className="min-h-screen relative theme-noir" style={{ backgroundColor: "#05060a" }}
      data-testid="cloner-page">
      <CircuitBg />
      <div className="relative z-10 max-w-5xl mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Copy className="w-6 h-6 text-[#FF8A2B]"
              style={{ filter: "drop-shadow(0 0 10px rgba(255,138,43,.5))" }} />
            <div className="leading-tight">
              <div className="font-heading text-xl text-white tracking-wide">CLONADOR</div>
              <div className="text-[10px] text-white/40 tracking-[0.2em]">COPIA · EDITA · DESCARGA</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {credits !== null && (
              <span className="glass-card px-3 py-2 rounded-xl text-xs text-[#FF8A2B]"
                data-testid="cloner-credits">{credits} créditos</span>
            )}
            <button onClick={() => nav("/muro")} data-testid="cloner-back"
              className="glass-btn px-3 py-2 rounded-xl text-xs flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Volver al muro
            </button>
          </div>        </div>

        <div className="glass-card forge-card p-5">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex-1 flex items-center gap-2 glass-card px-3 bg-black/40">
              <Globe className="w-4 h-4 text-[#FF8A2B] shrink-0" />
              <input data-testid="cloner-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && start()}
                placeholder="ejemplo.com"
                className="flex-1 bg-transparent py-3 text-white text-sm outline-none" />
            </div>
            <button data-testid="cloner-start-btn" onClick={start} disabled={busy}
              className="glass-btn glass-btn-red px-6 py-3 rounded-xl font-heading tracking-wider
                         flex items-center justify-center gap-2 shrink-0">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
              {busy ? "EMPEZANDO…" : `COPIAR · ${tierCredits} cr`}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-4">
            {TIERS.map((t) => (
              <button key={t.pages} data-testid={`cloner-tier-${t.pages}`} onClick={() => setTier(t.pages)}
                className={`matrix-chip ${tier === t.pages ? "is-active" : ""} px-3 py-1.5 rounded-lg text-[11px]`}>
                {t.label} · {t.credits} cr
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <button data-testid="cloner-js-toggle" onClick={() => setWithJs((v) => !v)}
              className="flex items-center gap-2 text-xs text-white/80">
              <span className={`w-10 h-6 rounded-full relative transition-colors ${
                withJs ? "bg-[#C2620E]" : "bg-white/15"}`}>
                <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${
                  withJs ? "left-5" : "left-1"}`} />
              </span>
              Copia exacta con JavaScript
            </button>
            <button data-testid="cloner-demo-btn" onClick={() => { setUrl("books.toscrape.com"); setTier(10); }}
              className="glass-btn px-3 py-1.5 rounded-xl text-[11px] flex items-center gap-1.5">
              <CircleDot className="w-3 h-3" /> Probar con un sitio de ejemplo (10 págs · 500 cr)
            </button>
            <span className="text-[11px] text-white/35">
              Copiamos HTML, CSS, JS, imágenes, fuentes y medios del sitio.
            </span>
          </div>
        </div>

        <div className="mt-8">
          <div className="text-sm text-white/70 mb-3">Tus sitios</div>
          {sites.length === 0 && (
            <p className="text-xs text-white/40" data-testid="cloner-empty">
              Todavía no has copiado ningún sitio.
            </p>
          )}
          <div className="grid sm:grid-cols-2 gap-3" data-testid="cloner-sites">
            {sites.map((s) => (
              <SiteCard key={s.site_id} site={s} onDelete={del} onStop={stop} onRename={rename} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
